import React, { useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts';
import { Box, ButtonBase, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { FileDownloadOutlined } from '@mui/icons-material';
import { ChartTooltipCard, ChartTooltipRow, EmptyState, LegendRow, LegendSwatch, Panel } from './Panel';
import { SeriesPoint } from './metrics';
import { Bucket, formatBucketLabel, formatBucketTick } from './period';
import { bodyFont, chart, formatCount, headingFont, ink, series, surface } from './tokens';

interface StampsRewardsChartProps {
  data: SeriesPoint[];
  bucket: Bucket;
  /** Describes the visible window, shown under the title. */
  rangeLabel: string;
  /** Slug used in export filenames. */
  locationName: string;
}

const SERIES = [
  { key: 'stamps' as const, label: 'Stamps', color: series.stamps },
  { key: 'issued' as const, label: 'Rewards issued', color: series.issued },
  { key: 'redeemed' as const, label: 'Rewards redeemed', color: series.redeemed }
];

const bucketNoun: Record<Bucket, string> = {
  day: 'day',
  week: 'week',
  month: 'month'
};

const slug = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'location';

/**
 * The page's primary chart: stamps issued alongside rewards earned and
 * redeemed. Rewards are stacked against each other but kept separate from
 * stamps, because a reward is not a stamp and stacking them would invite
 * reading one bar as a single total.
 */
export const StampsRewardsChart: React.FC<StampsRewardsChartProps> = ({
  data,
  bucket,
  rangeLabel,
  locationName
}) => {
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  // The panel's title sits outside the captured element, so a caption is
  // rendered into it for the duration of the capture — an exported PNG with no
  // idea which location or period it covers is not worth downloading.
  const [capturing, setCapturing] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const totals = data.reduce(
    (acc, point) => ({
      stamps: acc.stamps + point.stamps,
      issued: acc.issued + point.issued,
      redeemed: acc.redeemed + point.redeemed
    }),
    { stamps: 0, issued: 0, redeemed: 0 }
  );
  const hasData = totals.stamps + totals.issued + totals.redeemed > 0;

  const exportCsv = () => {
    const rows = [
      ['Period start', 'Stamps', 'Rewards issued', 'Rewards redeemed'],
      ...data.map((point) => [
        point.key,
        `${point.stamps}`,
        `${point.issued}`,
        `${point.redeemed}`
      ])
    ];
    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slug(locationName)}-stamps-rewards.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  };

  const exportPng = async () => {
    setExportAnchor(null);
    if (!bodyRef.current) return;
    setCapturing(true);
    try {
      // Let React paint the caption before html2canvas reads the DOM.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(bodyRef.current, {
        backgroundColor: surface.card,
        logging: false,
        useCORS: true,
        scale: 2
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${slug(locationName)}-stamps-rewards.png`;
      link.click();
    } catch (error) {
      console.error('Failed to export chart as an image:', error);
    } finally {
      setCapturing(false);
    }
  };

  const renderTooltip = ({ active, label }: TooltipProps<number, string>) => {
    if (!active) return null;
    const point = data.find((entry) => entry.key === label);
    if (!point) return null;
    return (
      <ChartTooltipCard title={formatBucketLabel(point.key, bucket)}>
        {SERIES.map((entry) => (
          <ChartTooltipRow
            key={entry.key}
            color={entry.color}
            label={entry.label}
            value={formatCount(point[entry.key])}
          />
        ))}
      </ChartTooltipCard>
    );
  };

  return (
    <Panel
      title='Stamps and rewards'
      subtitle={`Per ${bucketNoun[bucket]} · ${rangeLabel}`}
      hint='Stamps are scans by your staff. A reward is issued when a customer reaches a target, and redeemed when they claim it — which may fall in a later period.'
      action={
        <Box>
          <ButtonBase
            onClick={(event) => setExportAnchor(event.currentTarget)}
            disabled={!hasData}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              height: 34,
              px: '12px',
              border: `1px solid ${surface.borderStrong}`,
              borderRadius: '9px',
              fontFamily: bodyFont,
              fontWeight: 500,
              fontSize: 14,
              color: ink.body,
              '&:hover': { backgroundColor: surface.grid },
              '&.Mui-disabled': { color: ink.faint, opacity: 0.6 }
            }}
          >
            <FileDownloadOutlined sx={{ fontSize: 17 }} />
            Export
          </ButtonBase>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <MenuItem onClick={exportCsv}>
              <ListItemText primary='Download CSV' secondary='One row per period' />
            </MenuItem>
            <MenuItem onClick={exportPng}>
              <ListItemText primary='Download image' secondary='PNG of this chart' />
            </MenuItem>
          </Menu>
        </Box>
      }
      legend={
        <LegendRow>
          {SERIES.map((entry) => (
            <LegendSwatch
              key={entry.key}
              color={entry.color}
              label={entry.label}
              value={formatCount(totals[entry.key])}
            />
          ))}
        </LegendRow>
      }
    >
      <Box ref={bodyRef}>
        {capturing && (
          <Box sx={{ mb: '14px' }}>
            <Typography
              sx={{
                fontFamily: headingFont,
                fontWeight: 700,
                fontSize: 17,
                color: ink.strong
              }}
            >
              {locationName} — stamps and rewards
            </Typography>
            <Typography sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.muted }}>
              {`Per ${bucketNoun[bucket]} · ${rangeLabel}`}
            </Typography>
          </Box>
        )}
        {!hasData ? (
          <EmptyState
            height={chart.tallHeight}
            message='No stamps or rewards in this period. Pick a wider range, or start scanning to see activity here.'
          />
        ) : (
          <ResponsiveContainer width='100%' height={chart.tallHeight}>
            <BarChart data={data} margin={chart.margin} barGap={2}>
              <CartesianGrid vertical={false} stroke={chart.gridStroke} />
              <XAxis
                dataKey='key'
                tickFormatter={(key: string) => formatBucketTick(key, bucket)}
                tick={chart.tick}
                tickLine={false}
                axisLine={{ stroke: surface.border }}
                minTickGap={24}
              />
              <YAxis
                tick={chart.tick}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={44}
              />
              <Tooltip
                content={renderTooltip}
                cursor={{ fill: surface.grid, opacity: 0.7 }}
              />
              <Bar dataKey='stamps' fill={series.stamps} radius={[3, 3, 0, 0]} maxBarSize={30} />
              <Bar
                dataKey='issued'
                stackId='rewards'
                fill={series.issued}
                maxBarSize={30}
              />
              <Bar
                dataKey='redeemed'
                stackId='rewards'
                fill={series.redeemed}
                radius={[3, 3, 0, 0]}
                maxBarSize={30}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Panel>
  );
};

export default StampsRewardsChart;
